import { Button, Group, Stack, Text, TextInput, UnstyledButton } from '@mantine/core';
import { Patient } from '@medplum/fhirtypes';
import { KeyboardEvent, useState } from 'react';
import { HomeAddress, homeAddressError, readHomeAddress } from '@/services/patient-address';
import { PatientProfileEdit } from '@/services/patientProfile';

const NOT_ON_FILE = 'Not on file';

interface HomeAddressControlProps {
  label: string;
  patient: Patient | undefined;
  /** Saves through the row, which shows "Saved to profile"; absent means read-only. */
  save?: (edit: PatientProfileEdit) => Promise<boolean>;
  saving: boolean;
  /** Clears the row's "Saved to profile" / failure line when the editor reopens. */
  onStartEditing: () => void;
}

/** One or two lines: the street (and apt), then city, state and ZIP; whatever is on file. */
function shownLines(address: HomeAddress): string[] {
  const street = [address.line1, address.line2].filter(Boolean).join(', ');
  const place = [address.city, [address.state, address.postalCode].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  return [street, place].filter(Boolean);
}

/**
 * The home address on the PN card's profile row (BAR-3001), click-to-edit and
 * prefilled from `Patient.address`, where signup leaves only the ZIP. Street,
 * city, state and ZIP are required, so the backend never fills over it.
 */
export function HomeAddressControl({ label, patient, save, saving, onStartEditing }: HomeAddressControlProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<HomeAddress>(() => readHomeAddress(undefined));
  const [error, setError] = useState<string>();

  const lines = shownLines(readHomeAddress(patient));

  function startEditing(): void {
    setDraft(readHomeAddress(patient));
    setError(undefined);
    onStartEditing();
    setEditing(true);
  }

  async function submit(): Promise<void> {
    if (!save) return;
    const invalid = homeAddressError(draft);
    if (invalid) {
      setError(invalid);
      return;
    }
    setError(undefined);
    if (await save({ field: 'homeAddress', value: draft })) setEditing(false);
  }

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') void submit();
    if (event.key === 'Escape') setEditing(false);
  };
  const input = (field: keyof HomeAddress, placeholder: string, width: number | string, autoFocus = false) => (
    <TextInput
      size="xs"
      w={width}
      placeholder={placeholder}
      aria-label={`${label}, ${placeholder.toLowerCase()}`}
      data-testid={`home-address-${field}`}
      value={draft[field]}
      disabled={saving}
      autoFocus={autoFocus}
      onChange={(event) => {
        const value = event.currentTarget.value;
        setDraft((current) => ({ ...current, [field]: value }));
      }}
      onKeyDown={onKeyDown}
    />
  );

  if (editing) {
    return (
      <Stack gap={4} align="flex-end">
        {input('line1', 'Street and number', 260, true)}
        {input('line2', 'Apt or suite (optional)', 260)}
        <Group gap={4} wrap="nowrap">
          {input('city', 'City', 130)}
          {input('state', 'State', 56)}
          {input('postalCode', 'ZIP', 70)}
        </Group>
        {error && (
          <Text size="xs" c="red.7" role="alert">
            {error}
          </Text>
        )}
        <Group gap={4} wrap="nowrap">
          <Button size="xs" color="green.9" loading={saving} onClick={() => void submit()}>
            Save
          </Button>
          <Button size="xs" variant="default" disabled={saving} onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </Group>
      </Stack>
    );
  }

  const text = lines.length ? (
    lines.map((line, index) => (
      <Text key={index} size="sm" fw={500} ta="right" td={save ? 'underline dotted' : undefined}>
        {line}
      </Text>
    ))
  ) : (
    <Text size="sm" c="dimmed" ta="right" td={save ? 'underline dotted' : undefined}>
      {NOT_ON_FILE}
    </Text>
  );

  if (save) {
    return (
      <UnstyledButton onClick={startEditing} aria-label={`Edit ${label.toLowerCase()}`}>
        {text}
      </UnstyledButton>
    );
  }
  return <Stack gap={0}>{text}</Stack>;
}
